import React, { useState, useEffect, useMemo } from 'react';
import './index.css';
import LandingView from './components/LandingView';
import SlotPicker from './components/SlotPicker';
import SpecialtySelector from './components/SpecialtySelector';
import RegistrationForm from './components/RegistrationForm';
import QueueConfirmation from './components/QueueConfirmation';
import ClaimConfirmation from './components/ClaimConfirmation';

function Header({ solid }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fuera de la landing no hay hero violeta detrás: header siempre sólido.
  return (
    <header className={`site-header ${solid || scrolled ? 'site-header--scrolled' : ''}`}>
      <div className="header-inner">
        <a href="/" className="header-brand">
          <img src="/images/flor.png" alt="Daisies" className="header-flor" />
          <img src="/images/letras.png" alt="Daisies" className="header-letras" />
        </a>
        <a
          href="https://agenda.daisies.mx"
          target="_blank"
          rel="noopener noreferrer"
          className="header-cta press-scale"
        >
          Agendar
        </a>
      </div>
    </header>
  );
}

function App() {
  // Deep-link de confirmación de horario liberado: atencion.vercel.app/?t=<token>
  const confirmToken = useMemo(
    () => new URLSearchParams(window.location.search).get('t'),
    [],
  );
  // State machine: 'landing' → 'select-slot' / 'select-specialty' → 'register' → 'confirmed'
  const [step, setStep] = useState(confirmToken ? 'confirm' : 'landing');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [userData, setUserData] = useState(null);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const handleJoinQueue = () => {
    setSelectedSlot(null);
    setSelectedSpecialty(null);
    setStep('select-slot');
  };

  const handleJoinWaitlist = () => {
    setSelectedSlot(null);
    setSelectedSpecialty(null);
    setStep('select-specialty');
  };

  const handleBackToLanding = () => setStep('landing');

  const handleSlotSelected = (slot) => {
    setSelectedSlot(slot);
    setSelectedSpecialty(slot.specialty);
    setStep('register');
  };

  const handleSpecialtySelected = (specialty) => {
    setSelectedSpecialty(specialty);
    setStep('register');
  };

  const handleBackFromRegister = () => {
    if (selectedSlot) {
      setStep('select-slot');
    } else {
      setStep('select-specialty');
    }
  };

  const handleSubmit = (formData) => {
    setUserData({ ...formData, slot: selectedSlot, specialty: selectedSpecialty });
    setStep('confirmed');
  };

  return (
    <>
      <Header solid={step !== 'landing'} />
      {step === 'confirm' && <ClaimConfirmation token={confirmToken} />}
      {step === 'landing' && (
        <LandingView onJoinQueue={handleJoinQueue} onJoinWaitlist={handleJoinWaitlist} />
      )}
      {step === 'select-slot' && (
        <SlotPicker
          onBack={handleBackToLanding}
          onSelectSlot={handleSlotSelected}
          onJoinWaitlist={handleJoinWaitlist}
        />
      )}
      {step === 'select-specialty' && (
        <SpecialtySelector
          onBack={handleBackToLanding}
          onSelect={handleSpecialtySelected}
        />
      )}
      {step === 'register' && (
        <RegistrationForm
          onBack={handleBackFromRegister}
          onSubmit={handleSubmit}
          selectedSlot={selectedSlot}
          selectedSpecialty={selectedSpecialty}
        />
      )}
      {step === 'confirmed' && (
        <QueueConfirmation formData={userData} />
      )}
    </>
  );
}

export default App;
